// apps/admin/src/pages/SecurityAlertsPage.tsx
import React, { useState, useEffect, useCallback } from 'react';
import { collection, getDocs, query, orderBy, where, doc, updateDoc } from 'firebase/firestore';
import { db, functions } from '../firebaseConfig';
import { httpsCallable } from 'firebase/functions';
import {
  AlertTriangle,
  Shield,
  Clock,
  CheckCircle,
  AlertCircle,
  Filter,
  ChevronDown,
  ChevronUp,
  Eye,
  Bell,
  User,
  Database,
  Ban,
  CheckSquare,
  XCircle,
  FileText,
} from 'lucide-react';

interface AdminSecurityAlert {
  id: string;
  // `audit_write_failed` is raised directly by the grant callables, not derived by
  // detectSuspiciousPatterns, because a missing audit row is exactly what that
  // scheduler cannot see. Same document shape, so it renders here unchanged.
  type:
    | 'excessive_access'
    | 'after_hours'
    | 'excessive_exports'
    | 'rapid_company_access'
    | 'no_ticket'
    | 'audit_write_failed';
  severity: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
  adminUid: string;
  adminEmail: string;
  adminName: string;
  description: string;
  evidence: any;
  detectedAt: any;
  acknowledged: boolean;
  acknowledgedAt?: any;
  acknowledgedBy?: string;
  alertCategory: 'admin_attestation';
}

interface DataAccessAlert {
  id: string;
  type: 'anomalous_data_access';
  severity: 'low' | 'medium' | 'high' | 'critical';
  userId: string;
  userEmail: string;
  companyId: string;
  anomalies: Array<{
    type: string;
    severity: string;
    details: any;
    recommendation: string;
  }>;
  status: 'pending_review' | 'reviewed' | 'resolved' | 'dismissed';
  autoActions: string[];
  timestamp: any;
  reviewedAt?: any;
  reviewedBy?: string;
  resolution?: string;
  alertCategory: 'data_access';
}

type SecurityAlert = AdminSecurityAlert | DataAccessAlert;

interface Filters {
  severity?: string;
  category?: 'admin_attestation' | 'data_access';
  status?: string;
}

const SecurityAlertsPage: React.FC = () => {
  const [alerts, setAlerts] = useState<SecurityAlert[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedAlerts, setExpandedAlerts] = useState<Set<string>>(new Set());
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState<Filters>({});
  const [activeTab, setActiveTab] = useState<'all' | 'admin' | 'data_access'>('all');

  const fetchAlerts = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      const allAlerts: SecurityAlert[] = [];

      // Fetch admin attestation alerts
      const adminAlertsQuery = query(
        collection(db, 'admin_security_alerts'),
        orderBy('detectedAt', 'desc')
      );
      const adminSnapshot = await getDocs(adminAlertsQuery);
      const adminAlerts = adminSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        alertCategory: 'admin_attestation' as const,
      })) as AdminSecurityAlert[];
      allAlerts.push(...adminAlerts);

      // Fetch data access anomaly alerts via Cloud Function
      try {
        const getPlatformSecurityAlerts = httpsCallable(functions, 'getPlatformSecurityAlerts');
        const result = await getPlatformSecurityAlerts({ limit: 100 }) as any;
        const dataAccessAlerts = result.data.alerts.map((alert: any) => ({
          ...alert,
          alertCategory: 'data_access' as const,
        })) as DataAccessAlert[];
        allAlerts.push(...dataAccessAlerts);
      } catch (err) {
        console.warn('Could not fetch data access alerts:', err);
        // Continue with just admin alerts
      }

      // Sort all alerts by timestamp
      allAlerts.sort((a, b) => {
        const timeA = a.alertCategory === 'admin_attestation'
          ? (a as AdminSecurityAlert).detectedAt
          : (a as DataAccessAlert).timestamp;
        const timeB = b.alertCategory === 'admin_attestation'
          ? (b as AdminSecurityAlert).detectedAt
          : (b as DataAccessAlert).timestamp;

        const dateA = timeA?.toDate ? timeA.toDate() : new Date(timeA);
        const dateB = timeB?.toDate ? timeB.toDate() : new Date(timeB);
        return dateB.getTime() - dateA.getTime();
      });

      setAlerts(allAlerts);
    } catch (err: any) {
      console.error('Error fetching security alerts:', err);
      setError(err.message || 'Failed to load security alerts');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAlerts();
  }, [fetchAlerts]);

  const acknowledgeAlert = async (alertId: string, category: string) => {
    try {
      if (category === 'admin_attestation') {
        await updateDoc(doc(db, 'admin_security_alerts', alertId), {
          acknowledged: true,
          acknowledgedAt: new Date(),
        });

        setAlerts(prev =>
          prev.map(alert =>
            alert.id === alertId && alert.alertCategory === 'admin_attestation'
              ? { ...alert, acknowledged: true, acknowledgedAt: new Date() } as AdminSecurityAlert
              : alert
          )
        );
      } else {
        const updateSecurityAlert = httpsCallable(functions, 'updateSecurityAlert');
        await updateSecurityAlert({
          alertId,
          status: 'reviewed',
          resolution: 'Acknowledged by admin',
        });

        setAlerts(prev =>
          prev.map(alert =>
            alert.id === alertId && alert.alertCategory === 'data_access'
              ? { ...alert, status: 'reviewed', reviewedAt: new Date() } as DataAccessAlert
              : alert
          )
        );
      }
    } catch (err: any) {
      console.error('Error acknowledging alert:', err);
      alert('Failed to acknowledge alert: ' + err.message);
    }
  };

  const dismissAlert = async (alertId: string) => {
    try {
      const updateSecurityAlert = httpsCallable(functions, 'updateSecurityAlert');
      await updateSecurityAlert({
        alertId,
        status: 'dismissed',
        resolution: 'False positive - dismissed by admin',
      });

      setAlerts(prev =>
        prev.map(alert =>
          alert.id === alertId && alert.alertCategory === 'data_access'
            ? { ...alert, status: 'dismissed', reviewedAt: new Date() } as DataAccessAlert
            : alert
        )
      );
    } catch (err: any) {
      console.error('Error dismissing alert:', err);
      alert('Failed to dismiss alert: ' + err.message);
    }
  };

  const resolveAlert = async (alertId: string, resolution: string) => {
    try {
      const updateSecurityAlert = httpsCallable(functions, 'updateSecurityAlert');
      await updateSecurityAlert({
        alertId,
        status: 'resolved',
        resolution,
      });

      setAlerts(prev =>
        prev.map(alert =>
          alert.id === alertId && alert.alertCategory === 'data_access'
            ? { ...alert, status: 'resolved', resolution, reviewedAt: new Date() } as DataAccessAlert
            : alert
        )
      );
    } catch (err: any) {
      console.error('Error resolving alert:', err);
      alert('Failed to resolve alert: ' + err.message);
    }
  };

  const toggleExpanded = (alertId: string) => {
    setExpandedAlerts(prev => {
      const newSet = new Set(prev);
      if (newSet.has(alertId)) {
        newSet.delete(alertId);
      } else {
        newSet.add(alertId);
      }
      return newSet;
    });
  };

  const getNormalizedSeverity = (alert: SecurityAlert): string => {
    if (alert.alertCategory === 'admin_attestation') {
      return (alert as AdminSecurityAlert).severity;
    }
    return (alert as DataAccessAlert).severity.toUpperCase();
  };

  const getSeverityColor = (severity: string) => {
    const normalized = severity.toUpperCase();
    switch (normalized) {
      case 'CRITICAL':
        return 'bg-red-900 text-red-200 border-red-700';
      case 'HIGH':
        return 'bg-orange-900 text-orange-200 border-orange-700';
      case 'MEDIUM':
        return 'bg-yellow-900 text-yellow-200 border-yellow-700';
      default:
        return 'bg-blue-900 text-blue-200 border-blue-700';
    }
  };

  const getSeverityIcon = (severity: string) => {
    const normalized = severity.toUpperCase();
    switch (normalized) {
      case 'CRITICAL':
        return <AlertTriangle className="w-5 h-5" />;
      case 'HIGH':
        return <AlertCircle className="w-5 h-5" />;
      default:
        return <Shield className="w-5 h-5" />;
    }
  };

  const getAlertTitle = (alert: SecurityAlert): string => {
    if (alert.alertCategory === 'admin_attestation') {
      const adminAlert = alert as AdminSecurityAlert;
      const typeLabels: Record<string, string> = {
        excessive_access: 'Excessive Admin Access',
        after_hours: 'After-Hours Admin Activity',
        excessive_exports: 'Excessive Data Exports',
        rapid_company_access: 'Rapid Company Access',
        no_ticket: 'No Ticket Reference',
        audit_write_failed: 'Privileged Change Not Audited',
      };
      return typeLabels[adminAlert.type] || adminAlert.type;
    } else {
      const dataAlert = alert as DataAccessAlert;
      return `Data Access Anomalies (${dataAlert.anomalies.length})`;
    }
  };

  const formatDate = (timestamp: any) => {
    if (!timestamp) return 'Unknown';
    const date = timestamp?.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleString('en-AU', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  const applyFilter = (key: keyof Filters, value: any) => {
    setFilters(prev => ({
      ...prev,
      [key]: value || undefined,
    }));
  };

  const clearFilters = () => {
    setFilters({});
  };

  // Apply filters
  let filteredAlerts = alerts;

  if (activeTab !== 'all') {
    filteredAlerts = filteredAlerts.filter(a =>
      activeTab === 'admin' ? a.alertCategory === 'admin_attestation' : a.alertCategory === 'data_access'
    );
  }

  if (filters.severity) {
    filteredAlerts = filteredAlerts.filter(a => getNormalizedSeverity(a) === filters.severity?.toUpperCase());
  }

  if (filters.category) {
    filteredAlerts = filteredAlerts.filter(a => a.alertCategory === filters.category);
  }

  if (filters.status) {
    filteredAlerts = filteredAlerts.filter(a => {
      if (a.alertCategory === 'admin_attestation') {
        return filters.status === 'acknowledged'
          ? (a as AdminSecurityAlert).acknowledged
          : !(a as AdminSecurityAlert).acknowledged;
      } else {
        return (a as DataAccessAlert).status === filters.status;
      }
    });
  }

  // Calculate stats
  const totalAlerts = alerts.length;
  const adminAlerts = alerts.filter(a => a.alertCategory === 'admin_attestation').length;
  const dataAccessAlerts = alerts.filter(a => a.alertCategory === 'data_access').length;
  const criticalAlerts = alerts.filter(a => getNormalizedSeverity(a) === 'CRITICAL').length;
  const pendingAlerts = alerts.filter(a => {
    if (a.alertCategory === 'admin_attestation') {
      return !(a as AdminSecurityAlert).acknowledged;
    } else {
      return (a as DataAccessAlert).status === 'pending_review';
    }
  }).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-500"></div>
        <p className="ml-4 text-white">Loading Security Alerts...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 p-6 text-white">
      {/* Gradient Header */}
      <div className="relative overflow-hidden rounded-2xl mb-6">
        <div className="absolute inset-0 bg-gradient-to-br from-red-600 via-orange-600 to-yellow-600 opacity-90"></div>
        <div className="absolute top-0 right-0 w-64 h-64 bg-white opacity-5 rounded-full transform translate-x-32 -translate-y-32"></div>
        <div className="relative px-8 py-12">
          <div className="flex items-center gap-4 mb-3">
            <div className="p-3 bg-white bg-opacity-20 rounded-xl">
              <AlertTriangle className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-4xl font-bold text-white">Security Alerts</h1>
          </div>
          <p className="text-white opacity-80 text-lg">
            Comprehensive security monitoring: admin attestation patterns and broker data access anomalies
          </p>
          <div className="mt-6 flex items-center gap-4 text-white opacity-70">
            <div className="flex items-center gap-2">
              <User className="w-4 h-4" />
              <span className="text-sm">Admin monitoring</span>
            </div>
            <div className="flex items-center gap-2">
              <Database className="w-4 h-4" />
              <span className="text-sm">Data access tracking</span>
            </div>
            <div className="flex items-center gap-2">
              <Shield className="w-4 h-4" />
              <span className="text-sm">Real-time detection</span>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="mb-6 flex gap-2 border-b border-gray-700">
        <button
          onClick={() => setActiveTab('all')}
          className={`px-4 py-2 font-medium transition-colors ${
            activeTab === 'all'
              ? 'text-red-400 border-b-2 border-red-400'
              : 'text-gray-400 hover:text-white'
          }`}
        >
          All Alerts ({totalAlerts})
        </button>
        <button
          onClick={() => setActiveTab('admin')}
          className={`px-4 py-2 font-medium transition-colors ${
            activeTab === 'admin'
              ? 'text-red-400 border-b-2 border-red-400'
              : 'text-gray-400 hover:text-white'
          }`}
        >
          Admin Attestation ({adminAlerts})
        </button>
        <button
          onClick={() => setActiveTab('data_access')}
          className={`px-4 py-2 font-medium transition-colors ${
            activeTab === 'data_access'
              ? 'text-red-400 border-b-2 border-red-400'
              : 'text-gray-400 hover:text-white'
          }`}
        >
          Data Access Anomalies ({dataAccessAlerts})
        </button>
      </div>

      {/* Filters */}
      <div className="mb-6 space-y-4">
        <div className="flex gap-4">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-2 px-4 py-2 bg-gray-800 border border-gray-700 rounded-lg hover:border-red-500 transition-colors"
          >
            <Filter className="w-5 h-5" />
            Filters
            {showFilters ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
        </div>

        {showFilters && (
          <div className="p-4 bg-gray-800 border border-gray-700 rounded-lg space-y-4">
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Severity</label>
                <select
                  value={filters.severity || ''}
                  onChange={(e) => applyFilter('severity', e.target.value)}
                  className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-red-500"
                >
                  <option value="">All Severities</option>
                  <option value="CRITICAL">Critical</option>
                  <option value="HIGH">High</option>
                  <option value="MEDIUM">Medium</option>
                  <option value="LOW">Low</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Category</label>
                <select
                  value={filters.category || ''}
                  onChange={(e) => applyFilter('category', e.target.value)}
                  className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-red-500"
                >
                  <option value="">All Categories</option>
                  <option value="admin_attestation">Admin Attestation</option>
                  <option value="data_access">Data Access</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Status</label>
                <select
                  value={filters.status || ''}
                  onChange={(e) => applyFilter('status', e.target.value)}
                  className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-red-500"
                >
                  <option value="">All Status</option>
                  <option value="pending_review">Pending</option>
                  <option value="reviewed">Reviewed</option>
                  <option value="resolved">Resolved</option>
                  <option value="dismissed">Dismissed</option>
                  <option value="acknowledged">Acknowledged</option>
                </select>
              </div>
            </div>
            <div className="flex justify-end">
              <button
                onClick={clearFilters}
                className="px-4 py-2 text-sm text-gray-400 hover:text-white transition-colors"
              >
                Clear Filters
              </button>
            </div>
          </div>
        )}
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-900/20 border border-red-700 rounded-lg text-red-200">
          {error}
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <div className="p-4 bg-gray-800 border border-gray-700 rounded-lg">
          <div className="text-sm text-gray-400">Total Alerts</div>
          <div className="text-2xl font-bold text-white">{totalAlerts}</div>
        </div>
        <div className="p-4 bg-gray-800 border border-orange-700 rounded-lg">
          <div className="text-sm text-gray-400">Pending Review</div>
          <div className="text-2xl font-bold text-orange-400">{pendingAlerts}</div>
        </div>
        <div className="p-4 bg-gray-800 border border-red-700 rounded-lg">
          <div className="text-sm text-gray-400">Critical</div>
          <div className="text-2xl font-bold text-red-400">{criticalAlerts}</div>
        </div>
        <div className="p-4 bg-gray-800 border border-purple-700 rounded-lg">
          <div className="text-sm text-gray-400">Data Access</div>
          <div className="text-2xl font-bold text-purple-400">{dataAccessAlerts}</div>
        </div>
      </div>

      {/* Alerts */}
      {filteredAlerts.length === 0 ? (
        <div className="bg-gray-800 p-12 rounded-lg shadow-lg text-center border border-gray-700">
          <Shield className="w-16 h-16 mx-auto mb-4 text-gray-600" />
          <h3 className="text-xl font-semibold mb-2">No Security Alerts</h3>
          <p className="text-gray-400">No suspicious patterns detected.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredAlerts.map((alert) => {
            const isExpanded = expandedAlerts.has(alert.id);
            const severity = getNormalizedSeverity(alert);
            const isDataAccessAlert = alert.alertCategory === 'data_access';
            const dataAlert = isDataAccessAlert ? (alert as DataAccessAlert) : null;
            const adminAlert = !isDataAccessAlert ? (alert as AdminSecurityAlert) : null;

            const isPending = isDataAccessAlert
              ? dataAlert!.status === 'pending_review'
              : !adminAlert!.acknowledged;

            return (
              <div
                key={alert.id}
                className={`bg-gray-800 rounded-lg shadow-lg border ${
                  isPending ? 'border-red-700' : 'border-gray-700'
                } hover:border-gray-600 transition-colors`}
              >
                <div className="p-4">
                  <div className="flex items-start gap-4">
                    {/* Severity Icon */}
                    <div
                      className={`flex-shrink-0 w-10 h-10 rounded-full ${getSeverityColor(severity)} flex items-center justify-center font-bold border`}
                    >
                      {getSeverityIcon(severity)}
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      {/* Header */}
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <span className={`inline-block px-2 py-1 rounded text-xs font-semibold ${getSeverityColor(severity)} border mb-2`}>
                            {severity}
                          </span>
                          <span className="ml-2 inline-block px-2 py-1 rounded text-xs font-semibold bg-purple-900 text-purple-200 border border-purple-700">
                            {isDataAccessAlert ? <Database className="w-3 h-3 inline mr-1" /> : <User className="w-3 h-3 inline mr-1" />}
                            {isDataAccessAlert ? 'Data Access' : 'Admin Attestation'}
                          </span>
                        </div>
                        <div className="flex gap-2">
                          {isPending && (
                            <>
                              {isDataAccessAlert ? (
                                <>
                                  <button
                                    onClick={() => {
                                      const resolution = prompt('Enter resolution notes (or leave empty):');
                                      if (resolution !== null) {
                                        resolveAlert(alert.id, resolution || 'Reviewed and resolved');
                                      }
                                    }}
                                    className="flex items-center gap-1 px-3 py-1 bg-green-600 hover:bg-green-700 text-white rounded text-sm transition-colors"
                                  >
                                    <CheckCircle className="w-4 h-4" />
                                    Resolve
                                  </button>
                                  <button
                                    onClick={() => dismissAlert(alert.id)}
                                    className="flex items-center gap-1 px-3 py-1 bg-gray-600 hover:bg-gray-700 text-white rounded text-sm transition-colors"
                                  >
                                    <XCircle className="w-4 h-4" />
                                    Dismiss
                                  </button>
                                </>
                              ) : (
                                <button
                                  onClick={() => acknowledgeAlert(alert.id, alert.alertCategory)}
                                  className="flex items-center gap-1 px-3 py-1 bg-green-600 hover:bg-green-700 text-white rounded text-sm transition-colors"
                                >
                                  <CheckCircle className="w-4 h-4" />
                                  Acknowledge
                                </button>
                              )}
                            </>
                          )}
                        </div>
                      </div>

                      {/* Title & Description */}
                      <h3 className="text-white text-lg font-semibold mb-2">{getAlertTitle(alert)}</h3>

                      {isDataAccessAlert ? (
                        <>
                          <p className="text-gray-300 mb-2">
                            User <span className="font-semibold text-white">{dataAlert!.userEmail}</span> triggered{' '}
                            {dataAlert!.anomalies.length} security anomalies
                          </p>
                          {dataAlert!.autoActions.length > 0 && (
                            <div className="mb-2 flex items-center gap-2 text-red-400">
                              <Ban className="w-4 h-4" />
                              <span className="text-sm font-semibold">
                                Auto-Actions: {dataAlert!.autoActions.join(', ')}
                              </span>
                            </div>
                          )}
                        </>
                      ) : (
                        <p className="text-gray-300 mb-2">
                          <span className="font-semibold text-white">{adminAlert!.adminName}</span> - {adminAlert!.description}
                        </p>
                      )}

                      {/* Metadata */}
                      <div className="flex items-center gap-4 text-sm text-gray-400">
                        <span className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          {formatDate(isDataAccessAlert ? dataAlert!.timestamp : adminAlert!.detectedAt)}
                        </span>
                        {isDataAccessAlert ? (
                          <span className={`px-2 py-1 rounded text-xs font-semibold ${
                            dataAlert!.status === 'resolved' ? 'bg-green-900 text-green-200' :
                            dataAlert!.status === 'dismissed' ? 'bg-gray-700 text-gray-300' :
                            dataAlert!.status === 'reviewed' ? 'bg-blue-900 text-blue-200' :
                            'bg-orange-900 text-orange-200'
                          }`}>
                            {dataAlert!.status.replace('_', ' ')}
                          </span>
                        ) : (
                          adminAlert!.acknowledged && <span className="text-green-400">✓ Acknowledged</span>
                        )}
                        <button
                          onClick={() => toggleExpanded(alert.id)}
                          className="ml-auto text-blue-400 hover:text-blue-300 transition-colors text-xs flex items-center gap-1"
                        >
                          {isExpanded ? (
                            <>
                              <ChevronUp className="w-3 h-3" />
                              Hide details
                            </>
                          ) : (
                            <>
                              <ChevronDown className="w-3 h-3" />
                              Show details
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Expanded Details */}
                  {isExpanded && (
                    <div className="mt-4 pt-4 border-t border-gray-700">
                      {isDataAccessAlert ? (
                        <>
                          <h4 className="text-sm font-semibold text-gray-300 mb-3">Detected Anomalies</h4>
                          <div className="space-y-3">
                            {dataAlert!.anomalies.map((anomaly, idx) => (
                              <div key={idx} className="bg-gray-900 p-3 rounded-lg">
                                <div className="flex items-center gap-2 mb-2">
                                  <span className={`px-2 py-1 rounded text-xs font-semibold ${getSeverityColor(anomaly.severity)} border`}>
                                    {anomaly.severity}
                                  </span>
                                  <span className="text-white font-medium">{anomaly.type.replace(/_/g, ' ')}</span>
                                </div>
                                <p className="text-sm text-gray-400 mb-2">{anomaly.recommendation}</p>
                                <details className="text-xs text-gray-500">
                                  <summary className="cursor-pointer hover:text-gray-300">Technical details</summary>
                                  <pre className="mt-2 p-2 bg-black rounded overflow-x-auto">
                                    {JSON.stringify(anomaly.details, null, 2)}
                                  </pre>
                                </details>
                              </div>
                            ))}
                          </div>
                          {dataAlert!.resolution && (
                            <div className="mt-3 p-3 bg-green-900/20 border border-green-700 rounded-lg">
                              <div className="flex items-center gap-2 mb-1">
                                <FileText className="w-4 h-4 text-green-400" />
                                <span className="text-sm font-semibold text-green-400">Resolution</span>
                              </div>
                              <p className="text-sm text-gray-300">{dataAlert!.resolution}</p>
                            </div>
                          )}
                        </>
                      ) : (
                        <>
                          <h4 className="text-sm font-semibold text-gray-300 mb-3">Evidence</h4>
                          <pre className="text-xs text-gray-300 bg-gray-900 p-4 rounded overflow-x-auto font-mono">
                            {JSON.stringify(adminAlert!.evidence, null, 2)}
                          </pre>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default SecurityAlertsPage;
